import { useCallback, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useShowToast } from '@/hooks';
import { API_SERVICE } from '@/pages/reports/custom-reports/api/api';
import { REPORT_GRID_CONFIG } from '@/pages/reports/custom-reports/components/CustomReportTilesLayout/helpers';
import { TileType } from '@/pages/reports/custom-reports/types';

/**
 * Simple Report CRUD Hook
 *
 * Handles all report operations in local state, saves on demand.
 * No complex tracking, just simple and reliable CRUD operations.
 */

/**
 * Calculate smart positions for new blocks
 * Handles different tile types with varying widths (cards=3, tables=6, charts=4, etc.)
 * Finds the best available position without overlapping existing blocks
 * Priority: 1) Available space in existing rows, 2) Add to bottom
 */
const calculateNewBlockPositions = (existingLayout: any[], newItems: any[]) => {
  const GRID_WIDTH = 12;
  const DEFAULT_HEIGHT = 7;

  if (existingLayout.length === 0) {
    // No existing blocks, start from (0, 0) and position sequentially
    let currentX = 0;
    let currentY = 0;

    return newItems.map((item) => {
      // Check if current item fits in current row
      if (currentX + item.w > GRID_WIDTH) {
        // Move to next row
        currentX = 0;
        currentY += DEFAULT_HEIGHT;
      }

      const positionedItem = {
        ...item,
        x: currentX,
        y: currentY,
      };

      // Update position for next block
      currentX += item.w;

      return positionedItem;
    });
  }

  // Helper function to check if a position is free
  const isPositionFree = (
    x: number,
    y: number,
    w: number,
    h: number,
    layout: any[]
  ) => {
    return !layout.some(
      (existing) =>
        !(
          x >= existing.x + existing.w || // No horizontal overlap
          x + w <= existing.x ||
          y >= existing.y + existing.h || // No vertical overlap
          y + h <= existing.y
        )
    );
  };

  // Find available positions in existing rows first
  const findAvailablePosition = (item: any, currentLayout: any[]) => {
    if (currentLayout.length === 0) {
      return { x: 0, y: 0 };
    }

    // Try to fit in existing rows first - simple approach
    const uniqueRows = new Set(currentLayout.map((block) => block.y));
    const existingRows = Array.from(uniqueRows).sort((a, b) => a - b);

    for (const rowY of existingRows) {
      // Try positions from left to right in this row
      for (let x = 0; x <= GRID_WIDTH - item.w; x++) {
        if (isPositionFree(x, rowY, item.w, item.h, currentLayout)) {
          return { x, y: rowY };
        }
      }
    }

    // If no space in existing rows, add to bottom
    const maxY = Math.max(...currentLayout.map((block) => block.y + block.h));
    return { x: 0, y: maxY };
  };

  // Position new items with smart placement
  const positionedItems: any[] = [];
  let workingLayout = [...existingLayout];

  for (const item of newItems) {
    const position = findAvailablePosition(item, workingLayout);

    const positionedItem = {
      ...item,
      x: position.x,
      y: position.y,
    };

    positionedItems.push(positionedItem);
    workingLayout.push(positionedItem); // Add to working layout for next iteration
  }

  return positionedItems;
};

// Helper function to merge ref changes into state data
const mergeRefChangesIntoState = (currentData: any, stateRef: any) => {
  let updatedData = { ...currentData };

  // Merge metadata changes into tiles
  if (stateRef.current.metadataChanges.size > 0) {
    const updatedTiles = (updatedData?.layoutData?.tiles || []).map(
      (tile: any) => {
        const tileId = tile.id.toString();
        const metadataChanges = stateRef.current.metadataChanges.get(tileId);

        if (metadataChanges) {
          // Deep merge metadata, preserving existing properties
          const mergedMetadata = { ...tile.metadata, ...metadataChanges };
          if (tile.metadata.filters && metadataChanges.filters) {
            mergedMetadata.filters = {
              ...tile.metadata.filters,
              ...metadataChanges.filters,
            };
          }

          return {
            ...tile,
            metadata: mergedMetadata,
          };
        }

        return tile;
      }
    );

    updatedData = {
      ...updatedData,
      layoutData: {
        ...updatedData.layoutData,
        tiles: updatedTiles,
      },
    };
  }

  // Merge filter changes into filters
  if (stateRef.current.filterChanges.size > 0) {
    const existingFilters = updatedData?.layoutData?.filters || {};
    const mergedFilters = { ...existingFilters };

    stateRef.current.filterChanges.forEach((value, key) => {
      mergedFilters[key] = value;
    });

    updatedData = {
      ...updatedData,
      layoutData: {
        ...updatedData.layoutData,
        filters: mergedFilters,
      },
    };
  }

  return updatedData;
};

interface ReportCRUDConfig {
  reportData: any;
  reportId: string;
  setReportData: (data: any) => void;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  setEditMode: (editMode: boolean) => void;
  setIsSaving: (saving: boolean) => void;
  setIsLocalUpdate: (localUpdate: boolean) => void;
  onBlocksAdded?: (newBlockPositions: any[]) => void;
}

export const useReportCRUD = (config: ReportCRUDConfig) => {
  const {
    reportData,
    reportId,
    setReportData,
    setHasUnsavedChanges,
    setEditMode,
    setIsSaving,
    onBlocksAdded,
  } = config;

  const toast = useShowToast();
  const queryClient = useQueryClient();

  // Simple state tracking
  const stateRef = useRef({
    originalData: null as any,
    newBlocks: [] as any[], // Blocks with temp IDs
    blocksToDelete: [] as string[], // Real IDs to delete from server
    layoutChanged: false,
    currentLayout: null as any[],
    layoutResetKey: 0, // Key to force grid remount on cancel
    metadataChanges: new Map<string, any>(), // Track metadata changes by tile ID
    filterChanges: new Map<string, any>(), // Track filter changes by filter type
  });

  // Track if we're making local updates to avoid confusing them with cache refreshes
  const isLocalUpdateRef = useRef(false);

  // Initialize with original data and update when reportData changes (e.g., after refresh)
  useEffect(() => {
    if (reportData?.layoutData) {
      if (!stateRef.current.originalData) {
        stateRef.current.originalData = JSON.parse(JSON.stringify(reportData));
        stateRef.current.newBlocks = [];
        stateRef.current.blocksToDelete = [];
        stateRef.current.layoutChanged = false;
        stateRef.current.currentLayout = null;
        stateRef.current.metadataChanges.clear();
        stateRef.current.filterChanges.clear();

        // Initialize filter changes with existing API data
        if (reportData.layoutData?.filters) {
          Object.entries(reportData.layoutData.filters).forEach(
            ([key, value]) => {
              stateRef.current.filterChanges.set(key, value);
            }
          );
        }
      } else {
        // Only update original data if this is NOT a local update
        // Local updates should not be confused with cache refreshes
        if (!isLocalUpdateRef.current) {
          const hasChanges =
            stateRef.current.newBlocks.length > 0 ||
            stateRef.current.blocksToDelete.length > 0 ||
            stateRef.current.layoutChanged ||
            stateRef.current.metadataChanges.size > 0 ||
            stateRef.current.filterChanges.size > 0;

          // Only update if no changes AND the data actually changed
          const dataChanged =
            JSON.stringify(stateRef.current.originalData) !==
            JSON.stringify(reportData);

          if (!hasChanges && dataChanged) {
            stateRef.current.originalData = JSON.parse(
              JSON.stringify(reportData)
            );
          }
        } else {
          // Reset the local update flag after processing
          isLocalUpdateRef.current = false;
        }
      }
    }
  }, [reportData]);

  // Generate temp ID
  const generateTempId = useCallback(() => {
    return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Add blocks
  const handleAddBlocks = useCallback(
    (blocksToAdd: any[]) => {
      // First merge any pending ref changes into the current data
      const currentData = mergeRefChangesIntoState({ ...reportData }, stateRef);
      const newTiles = [];
      const newLayoutItems = [];

      blocksToAdd.forEach((block) => {
        const tempId = generateTempId();
        const trackingId = `${block.subject_type}_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`;

        // Create new tile
        const newTile = {
          id: tempId,
          subject_type: block.subject_type,
          metadata: {
            ...block.metadata,
            uniqueTrackingId: trackingId,
          },
          query: block.query,
        };

        // Track as new block
        stateRef.current.newBlocks.push({
          tempId,
          trackingId,
          data: newTile,
        });

        newTiles.push(newTile);

        // Get the correct dimensions for this tile type
        const componentType = block.metadata?.componentType || 'card';
        const sizeConfig =
          REPORT_GRID_CONFIG.TILE_SIZES[componentType as TileType] ||
          REPORT_GRID_CONFIG.TILE_SIZES[TileType.CARD];

        // Create layout item - will be positioned properly after all blocks are processed
        newLayoutItems.push({
          key: tempId,
          subject_type: block.subject_type, // Include subject_type for scroll targeting
          x: 0, // Will be calculated below
          y: 0, // Will be calculated below
          w: sizeConfig.w,
          h: sizeConfig.h,
          minW: sizeConfig.minW,
          minH: sizeConfig.minH,
          maxW: sizeConfig.maxW,
          maxH: sizeConfig.maxH,
        });
      });

      // Calculate smart positions for new blocks
      // Use ref layout if available (includes drag/resize changes), otherwise use state data
      const existingLayout =
        stateRef.current.currentLayout ||
        currentData?.layoutData?.layout?.data ||
        [];

      const positionedNewItems = calculateNewBlockPositions(
        existingLayout,
        newLayoutItems
      );

      // Update current data with null safety
      // Merge existing layout (from ref if available) with new positioned items
      const finalLayout = [...existingLayout, ...positionedNewItems];

      const updatedData = {
        ...currentData,
        layoutData: {
          ...currentData?.layoutData,
          tiles: [...(currentData?.layoutData?.tiles || []), ...newTiles],
          layout: {
            ...currentData?.layoutData?.layout,
            data: finalLayout,
          },
        },
      };

      // Update the ref to match the new state
      stateRef.current.currentLayout = finalLayout;

      // Mark as local update to prevent useEffect confusion
      isLocalUpdateRef.current = true;
      setReportData(updatedData);
      setHasUnsavedChanges(true);

      // Trigger scroll callback if provided
      onBlocksAdded?.(positionedNewItems);

      // Return positioned new items for scrolling
      return positionedNewItems;
    },
    [
      reportData,
      setReportData,
      setHasUnsavedChanges,
      generateTempId,
      onBlocksAdded,
    ]
  );

  // Remove block
  const handleRemoveBlock = useCallback(
    (blockId: string | number) => {
      const blockIdStr = blockId.toString();
      // First merge any pending ref changes into the current data
      const currentData = mergeRefChangesIntoState({ ...reportData }, stateRef);

      // Check if it's a temp block or real block
      if (blockIdStr.startsWith('temp_')) {
        // Remove from new blocks tracker
        stateRef.current.newBlocks = stateRef.current.newBlocks.filter(
          (block) => block.tempId !== blockIdStr
        );
      } else {
        // Add to delete list
        if (!stateRef.current.blocksToDelete.includes(blockIdStr)) {
          stateRef.current.blocksToDelete.push(blockIdStr);
        }
      }

      // Remove from current UI data with null safety
      const updatedTiles = (currentData?.layoutData?.tiles || []).filter(
        (tile: any) => tile.id.toString() !== blockIdStr
      );

      // CRITICAL: Use current layout from ref if available (includes drag/resize changes)
      // Otherwise fall back to state data
      const currentLayout =
        stateRef.current.currentLayout ||
        currentData?.layoutData?.layout?.data ||
        [];

      const updatedLayout = currentLayout.filter(
        (item: any) => item.key.toString() !== blockIdStr
      );

      const updatedData = {
        ...currentData,
        layoutData: {
          ...currentData?.layoutData,
          tiles: updatedTiles,
          layout: {
            ...currentData?.layoutData?.layout,
            data: updatedLayout,
          },
        },
      };

      // CRITICAL: Also update the layout ref to remove the deleted item
      stateRef.current.currentLayout = updatedLayout;

      // Mark as local update to prevent useEffect confusion
      isLocalUpdateRef.current = true;
      setReportData(updatedData);
      setHasUnsavedChanges(true);
    },
    [reportData, setReportData, setHasUnsavedChanges]
  );

  // Handle layout change - store in ref only, no state update to avoid re-renders
  const handleLayoutChange = useCallback(
    (newLayout: any[]) => {
      // Store layout changes in ref only - no state update to avoid re-renders during drag/resize
      stateRef.current.currentLayout = newLayout;
      stateRef.current.layoutChanged = true;

      // Track unsaved changes via callback
      setHasUnsavedChanges(true);
    },
    [setHasUnsavedChanges]
  );

  const onTileMetaChange = useCallback(
    (tileId: string, metadata: any) => {
      const state = stateRef.current;

      // Ensure we always use string keys for consistency
      const stringTileId = tileId.toString();

      // Track metadata changes for this tile in ref only
      const existingChanges = state.metadataChanges.get(stringTileId) || {};

      // Deep merge filters if both existing and new have filters
      const updatedChanges = { ...existingChanges, ...metadata };
      if (existingChanges.filters && metadata.filters) {
        updatedChanges.filters = {
          ...existingChanges.filters,
          ...metadata.filters,
        };
      }

      state.metadataChanges.set(stringTileId, updatedChanges);

      // Track unsaved changes via callback
      setHasUnsavedChanges(true);
    },
    [setHasUnsavedChanges]
  );

  // Filter change handler - similar to onTileMetaChange
  const onFilterChange = useCallback(
    (filterType: string, filterData: any) => {
      const state = stateRef.current;

      // Track filter changes for this filter type in ref only
      const existingChanges = state.filterChanges.get(filterType) || {};

      // Smart merge based on data type
      let updatedChanges;

      if (Array.isArray(filterData)) {
        // For arrays (like normalFilters), replace completely
        updatedChanges = filterData;
      } else if (typeof filterData === 'object' && filterData !== null) {
        // Special handling for timeframe filters to avoid mixed data types
        if (filterType === 'timeframe') {
          // For timeframe filters, replace completely to avoid mixing string and date types
          updatedChanges = { ...filterData };
        } else {
          // For other objects, deep merge to preserve existing properties
          updatedChanges = { ...existingChanges, ...filterData };

          // Deep merge nested objects if they exist
          if (existingChanges.filters && filterData.filters) {
            updatedChanges.filters = {
              ...existingChanges.filters,
              ...filterData.filters,
            };
          }

          // Deep merge other nested objects
          Object.keys(existingChanges).forEach((key) => {
            if (
              typeof existingChanges[key] === 'object' &&
              existingChanges[key] !== null &&
              !Array.isArray(existingChanges[key]) &&
              filterData[key] &&
              typeof filterData[key] === 'object' &&
              !Array.isArray(filterData[key])
            ) {
              updatedChanges[key] = {
                ...existingChanges[key],
                ...filterData[key],
              };
            }
          });
        }
      } else {
        // For primitives, replace completely
        updatedChanges = filterData;
      }

      state.filterChanges.set(filterType, updatedChanges);

      // Track unsaved changes via callback
      setHasUnsavedChanges(true);
    },
    [setHasUnsavedChanges]
  );

  // Save all changes
  const handleSaveChanges = useCallback(async () => {
    if (!reportId) {
      return;
    }

    const state = stateRef.current;

    // Check if there are actually any changes to save
    const hasChanges =
      state.newBlocks.length > 0 ||
      state.blocksToDelete.length > 0 ||
      state.layoutChanged ||
      state.metadataChanges.size > 0 ||
      state.filterChanges.size > 0;

    if (!hasChanges) {
      return;
    }

    setIsSaving(true);

    try {
      const idMapping = new Map<string, string>();

      // STEP 1: Delete blocks
      if (state.blocksToDelete.length > 0) {
        await API_SERVICE.deleteTiles(reportId, {
          ids: state.blocksToDelete,
        });
      }

      // STEP 2: Add new blocks
      if (state.newBlocks.length > 0) {
        const tilesPayload = state.newBlocks.map((block) => ({
          subject_type: block.data.subject_type,
          metadata: block.data.metadata,
          query: block.data.query,
        }));

        const response = await API_SERVICE.addTiles(reportId, {
          tiles: tilesPayload,
        });

        // Map temp IDs to real IDs
        const apiTiles = response?.tiles || response?.data?.tiles || response;
        if (apiTiles && Array.isArray(apiTiles)) {
          apiTiles.forEach((apiTile: any) => {
            const matchingBlock = state.newBlocks.find(
              (block) => block.trackingId === apiTile.metadata?.uniqueTrackingId
            );
            if (matchingBlock) {
              idMapping.set(matchingBlock.tempId, apiTile.id.toString());
            }
          });
        }
      }

      // STEP 3: Update layout and tiles with real IDs
      // We need to update if:
      // 1. Layout was changed (drag/resize)
      // 2. New blocks were added (need their positions in layout)
      // 3. Blocks were deleted (layout structure changed)
      // 4. Metadata was changed
      const needsLayoutUpdate =
        state.layoutChanged ||
        state.newBlocks.length > 0 ||
        state.blocksToDelete.length > 0;

      const needsMetadataUpdate = state.metadataChanges.size > 0;
      const needsFilterUpdate = state.filterChanges.size > 0;

      // Prepare update payload
      const updatePayload: any = {};

      // Add layout data if needed
      if (needsLayoutUpdate) {
        const layoutToSave =
          state.currentLayout || reportData?.layoutData?.layout?.data || [];

        if (layoutToSave.length > 0) {
          const layoutWithRealIds = layoutToSave.map((item: any) => {
            const realId = idMapping.get(item.key) || item.key;
            return {
              ...item,
              key: realId,
            };
          });
          updatePayload.layout = { data: layoutWithRealIds };
        }
      }

      // Add tiles data with metadata changes if needed
      if (needsMetadataUpdate) {
        const currentTiles = reportData?.layoutData?.tiles || [];
        const updatedTiles = currentTiles.map((tile: any) => {
          // First, update the tile ID if it was a temp ID that got a real ID
          const realId = idMapping.get(tile.id.toString()) || tile.id;
          const tileId = realId.toString();

          // Get metadata changes using the original temp ID (if it was a temp ID)
          const originalTileId = tile.id.toString();
          const metadataChanges = state.metadataChanges.get(originalTileId);

          if (metadataChanges) {
            // If this was a temp ID, update the metadata changes map to use the real ID
            if (idMapping.has(originalTileId)) {
              state.metadataChanges.delete(originalTileId);
              state.metadataChanges.set(tileId, metadataChanges);
            }

            // Deep merge filters to preserve existing filters
            const mergedMetadata = { ...tile.metadata, ...metadataChanges };
            if (tile.metadata.filters && metadataChanges.filters) {
              mergedMetadata.filters = {
                ...tile.metadata.filters,
                ...metadataChanges.filters,
              };
            }

            return {
              ...tile,
              id: typeof realId === 'string' ? parseInt(realId, 10) : realId, // Ensure numeric ID
              metadata: mergedMetadata,
            };
          }

          // Even if no metadata changes, update the ID if it was a temp ID
          return {
            ...tile,
            id: typeof realId === 'string' ? parseInt(realId, 10) : realId, // Ensure numeric ID
          };
        });

        updatePayload.tiles_attributes = updatedTiles;
      }

      // Add filter data if needed
      if (needsFilterUpdate) {
        const filterData: any = {};

        // Start with existing API filters as base
        const existingFilters = reportData?.layoutData?.filters;
        if (existingFilters) {
          Object.assign(filterData, existingFilters);
        }

        // Apply filter changes on top of existing filters
        state.filterChanges.forEach((value, key) => {
          filterData[key] = value;
        });

        updatePayload.filters = filterData;
      }

      // Make single API call if there's anything to update
      if (Object.keys(updatePayload).length > 0) {
        await API_SERVICE.updateCustomReport(reportId, updatePayload);
      }

      // Update report data with real IDs for new blocks
      if (state.newBlocks.length > 0) {
        const updatedReportData = { ...reportData };
        const updatedTiles = (updatedReportData?.layoutData?.tiles || []).map(
          (tile: any) => {
            const realId = idMapping.get(tile.id.toString());
            if (realId) {
              return {
                ...tile,
                id: typeof realId === 'string' ? parseInt(realId, 10) : realId,
              };
            }
            return tile;
          }
        );

        updatedReportData.layoutData = {
          ...updatedReportData.layoutData,
          tiles: updatedTiles,
        };

        // Update the original data to reflect the new state
        state.originalData = updatedReportData;
        setReportData(updatedReportData);
      }

      // Clear all changes
      state.newBlocks = [];
      state.blocksToDelete = [];
      state.layoutChanged = false;
      state.currentLayout = null;
      state.metadataChanges.clear();
      state.filterChanges.clear();
      setHasUnsavedChanges(false);

      setEditMode(false);

      // Invalidate React Query cache to refresh the data
      queryClient.invalidateQueries({
        queryKey: [`getCustomReports/${reportId}`],
      });

      toast({
        title: 'Report saved successfully',
        status: 'success',
      });
    } catch (error) {
      toast({
        title: 'Failed to save report',
        description: error?.message || 'Unknown error',
        status: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    reportId,
    setIsSaving,
    setEditMode,
    setHasUnsavedChanges,
    toast,
    reportData,
    setReportData,
    queryClient,
  ]);

  // Cancel changes
  const handleCancelChanges = useCallback(() => {
    const state = stateRef.current;

    // Reset to original data
    if (state.originalData) {
      // Mark as local update to prevent useEffect confusion
      isLocalUpdateRef.current = true;
      setReportData(state.originalData);

      // Also reset the layout ref to match original data
      state.currentLayout =
        state.originalData?.layoutData?.layout?.data || null;

      // Increment layout reset key to force grid remount
      state.layoutResetKey += 1;
    }

    // Clear all changes
    state.newBlocks = [];
    state.blocksToDelete = [];
    state.layoutChanged = false;
    state.currentLayout = null;
    state.metadataChanges.clear();
    state.filterChanges.clear();
    setHasUnsavedChanges(false);

    setEditMode(false);
  }, [setReportData, setEditMode, setHasUnsavedChanges]);

  // Function to check if there are unsaved changes (for external components)
  const hasUnsavedChanges = useCallback(() => {
    const state = stateRef.current;
    return (
      state.newBlocks.length > 0 ||
      state.blocksToDelete.length > 0 ||
      state.layoutChanged ||
      state.metadataChanges.size > 0 ||
      state.filterChanges.size > 0
    );
  }, []);

  // Function to sync footer state with actual changes
  const syncFooterState = useCallback(() => {
    const actualHasChanges = hasUnsavedChanges();
    setHasUnsavedChanges(actualHasChanges);
  }, [hasUnsavedChanges, setHasUnsavedChanges]);

  // Function to clear filter changes (useful when entering edit mode)
  const clearFilterChanges = useCallback(() => {
    stateRef.current.filterChanges.clear();
    setHasUnsavedChanges(false);
  }, [setHasUnsavedChanges]);

  // Function to get current merged data (including ref changes)
  const getCurrentMergedData = useCallback(() => {
    return mergeRefChangesIntoState({ ...reportData }, stateRef);
  }, [reportData]);

  return {
    handleAddBlocks,
    handleRemoveBlock,
    handleLayoutChange,
    handleSaveChanges,
    handleCancelChanges,
    onTileMetaChange,
    onFilterChange,
    hasUnsavedChanges,
    syncFooterState,
    clearFilterChanges,
    getCurrentMergedData,
    layoutResetKey: stateRef.current.layoutResetKey,
  };
};
